import { AUTH_COOKIE, readCookie } from '@artic/ui/lib/auth-cookies';
import type { User } from '@supabase/supabase-js';

import { supabase } from './supabaseClient';

/**
 * Synchronous read of the current access token from the artic_token cookie.
 * The cookie is the single source of truth — it's written by api-client's
 * refresh path (`custom-fetch.ts`) and `App.tsx#syncCookies`, and read by
 * every authenticated microsite (hub, quality). Calling
 * `supabase.auth.getSession()` inline just to grab the token is what dragged
 * the refresh-retry surface into ~50 hand-written `api.ts` headers
 * (Sentry ARTIC-HUB-12X).
 */
export function getAuthToken(): string | null {
  return readCookie(AUTH_COOKIE) ?? null;
}

/**
 * Best-effort current-user fetch. Swallows transient supabase-auth-js
 * failures (429 rate-limit, network blip, refresh death) and returns null
 * instead of letting an unhandled promise rejection escape to
 * `onunhandledrejection`. Use for non-critical reads like Intercom identify
 * or timeline author lookups — anywhere a missing user is degraded UX
 * rather than a hard failure.
 */
export async function getCurrentUserSafe(): Promise<User | null> {
  try {
    const { data, error } = await supabase.auth.getUser();
    if (error) return null;
    return data.user;
  } catch {
    return null;
  }
}
